"use client"
import { createContext, useContext, useState, useEffect } from "react";
import { createClient } from '@/lib/supabase/client';
import AuthModal from "@/components/auth/authModal";
import Modal from "@/components/generic/modal";
import { useRouter } from "next/navigation";
import { getUserdata } from "@/app/actions";

const _UserContext = createContext(null);

export const UserContextProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authResponseModalOpen, setAuthResponseModalOpen] = useState(false);
    const [error, setError] = useState(null);

    const router = useRouter();

    const setUserWithData = async (user) => {
        if (!user) {
            setUser(null);
            return;
        }

        const userdata = await getUserdata(user.id);
        setUser({ ...user, ...userdata });
    }

    useEffect(() => {
        async function getUserSession() {
            const supabase = createClient();
            const { data: { user } } = await supabase.auth.getUser()
            const { data: { session } } = await supabase.auth.getSession();

            const hash = window.location.hash.substring(1);
            const params = new URLSearchParams(hash);
            const access_token = params.get('access_token');
            const refresh_token = params.get('refresh_token');

            if (access_token && refresh_token) {
                const { data, error } = await supabase.auth.setSession({
                    access_token,
                    refresh_token,
                });

                const showModal = data.session?.user?.app_metadata?.provider ? false : true;

                if (error || !data.session) {
                    setLoading(false);
                    setError("There was an error with your signup. Please contact an administrator.");
                    setAuthResponseModalOpen(showModal);
                    router.replace(window.location.pathname);
                    return;
                }

                setUserWithData(data.session.user);
                setLoading(false);
                setAuthResponseModalOpen(showModal);
                router.replace(window.location.pathname);

                if (window.opener) {
                    window.opener.postMessage({ type: 'AUTH_COMPLETE' }, window.location.origin);
                    setTimeout(() => window.close(), 3500);
                }

                return;
            }

            if (user && !session) {
                setUserWithData(user);
                setLoading(false);
                return;
            }

            setUserWithData(session?.user ?? null);
            setLoading(false);

            const { data: authListener } = supabase.auth.onAuthStateChange(
                (event, session) => {
                    setUserWithData(session?.user ?? null);
                }
            );

            return () => {
                authListener?.subscription.unsubscribe();
            };
        }

        setLoading(true);
        getUserSession();
    }, [router]);

    return (
        <_UserContext.Provider value={{ user, setUser, setAuthModalOpen, loading, setLoading }}>
            {children}
            <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
            <Modal open={authResponseModalOpen} onOpenChange={setAuthResponseModalOpen} >
                <span hidden={error}>
                    Your email was sucessfully verified and you have been signed in automatically.
                    Thank you for trying out Black Market!
                </span>
                <span className="text-red-500" hidden={!error}>
                    {error}
                </span>
            </Modal>
        </_UserContext.Provider>
    );
};

export const UserContext = () => useContext(_UserContext);
